import {
  collection,
  doc,
  FirestoreDataConverter,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { useMemo } from 'react';
import { firestore } from '@/lib/firebase';

/** How long a "live view" lock keeps a box connected / commands greyed out. */
export const LIVE_VIEW_MINUTES = 30;

export interface LiveBox {
  ip: string;
  until: Date | null;
}

const converter: FirestoreDataConverter<LiveBox> = {
  toFirestore: () => {
    throw new Error('use startLiveView/endLiveView');
  },
  fromFirestore: (snapshot) => {
    const d = snapshot.data();
    return { ip: d.ip, until: d.until?.toDate?.() ?? null };
  },
};

export function useLiveBoxes() {
  const ref = useMemo(
    () => collection(firestore, 'liveBoxes').withConverter(converter),
    []
  );
  return useCollectionData(ref, {
    snapshotListenOptions: { includeMetadataChanges: false },
  });
}

/** Set/refresh a box's live-view lock — the agent connects it, commands grey. */
export function startLiveView(ip: string) {
  return setDoc(doc(firestore, 'liveBoxes', ip), {
    ip,
    until: Timestamp.fromMillis(Date.now() + LIVE_VIEW_MINUTES * 60_000),
  });
}

export function endLiveView(ip: string) {
  // Mark expired (not delete) so the agent's reconcile sees it, runs
  // `adb disconnect`, then removes it. Deleting outright would leave the box
  // connected because the agent only iterates existing lock docs.
  return setDoc(doc(firestore, 'liveBoxes', ip), {
    ip,
    until: Timestamp.fromMillis(0),
  });
}

/** IPs with an active (non-expired) lock. */
export function useLiveIps(): Set<string> {
  const [liveBoxes] = useLiveBoxes();
  return useMemo(() => {
    const now = Date.now();
    return new Set(
      (liveBoxes ?? [])
        .filter((b) => b.until && b.until.getTime() > now)
        .map((b) => b.ip)
    );
  }, [liveBoxes]);
}
